import { BatchDto } from "@juicy/models/dto";
import { BatchCommentDto } from "@juicy/models/src/dto/AddBatchDto";
import { Button, Card, Modal, Text } from "@ui-kitten/components";
import React, { useEffect, useState } from "react";
import { StyleSheet, View } from "react-native";
import { useToast } from "react-native-toast-notifications";
import { apiService } from "../../App";
import { DataListType, useDataList } from "../common/dataList/useDataList";
import { ScreenLayout } from "../common/layout/ScreenLayout";
import { AddCommentForm } from "./AddCommentForm";
import { BatchListItem } from "./BatchListItem";
import { useNavigation } from "@react-navigation/native";
import { BottomTabNavigation } from "../navigation";

interface BatchShowScreenProps {
    batchId: string;
}

export const BatchShowScreen: React.FC<BatchShowScreenProps> = ({ batchId }) => {
    const { data, setData, refresh } = useDataList(DataListType.Batch);
    const [batch, setBatch] = useState<BatchDto | null>(null);
    const [commentList, setCommentList] = useState<BatchCommentDto[]>([]);
    const [showCommentForm, setShowCommentForm] = useState(false);
    const [commentToEdit, setCommentToEdit] = useState<BatchCommentDto | undefined>(undefined);
    const toast = useToast();
    const navigation = useNavigation<BottomTabNavigation>();

    const handleSave = async (batch: BatchDto) => {
        await apiService.batch.edit(batch);

        setData((prev) => {
            const newDataList: BatchDto[] = [...prev.list];
            const itemIndex = newDataList.findIndex(
                (x) =>
                    x.userId === batch.userId &&
                    x.recipeId === batch.recipeId &&
                    x.recipeVersion === batch.recipeVersion &&
                    x.batchNumber === batch.batchNumber
            );
            if (itemIndex !== -1) {
                newDataList[itemIndex] = { ...batch };
                toast.show("Rating Saved", { type: "success" });
                return {
                    ...prev,
                    list: newDataList,
                };
            } else {
                return prev;
            }
        });
    };

    const deleteBatch = async () => {
        if (batch) {
            await apiService.batch.delete(batch);
            navigation.navigate("BatchStackScreen", { screen: "BatchListScreen" });
        }
    };

    useEffect(() => {
        const list: BatchDto[] = data.list;
        if (list.length > 0) {
            const keys = batchId.split("-").map((x) => parseInt(x, 10));
            const batch = list.find(
                (b) =>
                    b.userId === keys[0] &&
                    b.recipeId === keys[1] &&
                    b.recipeVersion === keys[2] &&
                    b.batchNumber === keys[3]
            );
            if (batch) {
                setBatch(batch);
                setCommentList(batch.comments);
            }
        } else {
            void refresh();
        }
    }, [batchId, data.list, refresh]);

    const handleCommentAdded = (comment: BatchCommentDto) => {
        setCommentList((prev) => [comment, ...prev]);
        setShowCommentForm(false);
    };

    const handleCommentEdited = (comment: BatchCommentDto) => {
        const newCommentList = [...commentList];
        const index = newCommentList.findIndex((c) => c.id === comment.id);
        newCommentList[index] = comment;

        setCommentList(newCommentList);
        setCommentToEdit(undefined);
    };

    const handleCommentDeleted = (result: boolean) => {
        if (commentToEdit && result) {
            const newCommentList = [...commentList];
            const index = newCommentList.findIndex((c) => c.id === commentToEdit.id);
            newCommentList.splice(index, 1);

            setCommentList(newCommentList);
            setCommentToEdit(undefined);
        }
    };

    return (
        <ScreenLayout scroll={true} title="Batches">
            <View
                style={{
                    flexDirection: "row",
                    justifyContent: "flex-start",
                    alignItems: "center",
                }}
            ></View>
            {batch ? (
                <View>
                    <BatchListItem batch={batch!} ratingExpanded={true} handleSave={handleSave} />
                    {showCommentForm ? (
                        <AddCommentForm
                            batch={batch}
                            onCommentAdded={handleCommentAdded}
                            onCloseForm={() => setShowCommentForm(false)}
                        />
                    ) : (
                        <View>
                            <Button onPress={() => setShowCommentForm(true)} style={{ marginTop: 10 }} size="small">
                                Add Comment
                            </Button>
                            <Button status="danger" onPress={() => deleteBatch()} style={{ marginTop: 10 }} size="small">
                                Delete Batch
                            </Button>
                        </View>
                    )}
                    {commentList.length > 0 && <Text style={{ marginTop: 10 }}>Comments</Text>}
                    <Modal
                        visible={commentToEdit !== undefined}
                        backdropStyle={styles.backdrop}
                        onBackdropPress={() => setCommentToEdit(undefined)}
                    >
                        <AddCommentForm
                            batch={batch}
                            onCommentEdited={handleCommentEdited}
                            onCommentDeleted={handleCommentDeleted}
                            commentToEdit={commentToEdit}
                        />
                    </Modal>
                    {commentList.map((c: BatchCommentDto) => (
                        <Card key={c.id} onPress={() => setCommentToEdit(c)}>
                            <View
                                style={{
                                    display: "flex",
                                    justifyContent: "space-between",
                                    flexDirection: "row",
                                }}
                            >
                                <Text category="s2">{c.nDaysAfter} days steep</Text>
                                {/* <Rating size="small" disabled={true} rating={c.rating} /> */}
                            </View>
                            <Text category="p1">{c.comment}</Text>
                        </Card>
                    ))}
                </View>
            ) : (
                <></>
            )}
        </ScreenLayout>
    );
};

const styles = StyleSheet.create({
    container: {
        minHeight: 192,
    },
    backdrop: {
        backgroundColor: "rgba(0, 0, 0, 0.5)",
    },
});
